/**
 * File: useTodayBodyController.ts
 * Path: apps/admin/src/features/today/hooks/useTodayBodyController.ts
 * Description: Loads what the Today page adds up for the chosen dates and works out the overview, with the
 * weekly / monthly grouping of the money-in graph. "Owed to you" is the books' Trade debtors (invoiced and
 * unpaid), the figure Money and the accounting pages show; without the books it falls back to the contracts.
 * With the books, Needs attention lists each overdue invoice too.
 */

import { useMemo, useState } from "react";
import { openInvoices } from "@idk/config";
import { useInvoiceLedger } from "@/shared/hooks/useInvoiceLedgerQueries";
import { useCrmSettings } from "@/shared/hooks/useCrmSettingsQueries";
import { useNumbersInput } from "@/shared/hooks/useNumbersQueries";
import { buildTodayOverview, loadFrom, type DayRange, type Grain } from "@/features/today/utils/todayOverviewUtils";

export const useTodayBodyController = (range: DayRange, today: string) => {
  const { settings } = useCrmSettings();
  const { input, failed } = useNumbersInput(loadFrom(range, today), range.from);
  const [grain, setGrain] = useState<Grain>("month");
  const { ledger } = useInvoiceLedger();
  const books = ledger && ledger !== "unavailable" ? ledger : null;
  const overview = useMemo(() => (input ? buildTodayOverview(input, settings, range, grain, books) : null), [input, settings, range, grain, books]);
  const debtors = useMemo(
    () =>
      ledger && ledger !== "unavailable"
        ? { cents: ledger.controlCents, lateCount: openInvoices(ledger).filter((r) => r.status === "overdue").length }
        : null,
    [ledger],
  );
  return { failed, overview, debtors, grain, setGrain };
};
